import * as Phaser from 'phaser'
import { screenToWorld } from '../engine/iso'

export type Facing = 'n' | 'e' | 's' | 'w'

// Speed constant per CONTEXT.md: ~4 tiles/sec with 32px half-height → 128 px/s.
const SPEED = 128

/**
 * Pure helper — exported for unit tests (no Phaser dep). The atlasKey prefix
 * keeps player/npc-a/npc-b animation keys in separate namespaces in Phaser's
 * global anims registry so the three atlases coexist without key collision.
 *
 * Facing selection: the dominant axis wins (|y| ≥ |x| → vertical; else horizontal).
 * Ties (|x| === |y|) resolve to vertical to match the classic top-down idiom
 * where diagonal movement reads as forward/back rather than sideways.
 */
export function animationKeyFromVector(
  v: { x: number; y: number },
  lastFacing: Facing,
  atlasKey: string,
): { key: string; facing: Facing; walking: boolean } {
  if (v.x === 0 && v.y === 0) {
    return { key: `${atlasKey}-idle-${lastFacing}`, facing: lastFacing, walking: false }
  }
  const facing: Facing =
    Math.abs(v.y) >= Math.abs(v.x) ? (v.y > 0 ? 's' : 'n') : v.x > 0 ? 'e' : 'w'
  return { key: `${atlasKey}-walk-${facing}`, facing, walking: true }
}

/**
 * Player — arcade-physics sprite controlled by InputSystem.applyVector.
 *
 * Hard invariants (enforced by acceptance greps, ENGN-02/ENGN-04, MOVE-04):
 *  - Foot-anchored origin (0.5, 1.0) for correct iso depth sort
 *  - tileX/tileY kept in sync with render position via screenToWorld (Plan 02)
 *  - No import of KeyboardSource/JoystickSource/TapToWalkSource (MOVE-04)
 *  - No raw iso arithmetic in this file; iso.ts is the single source of truth
 *  - Math.round on render position writes (ENGN-04 pixel snap)
 *  - Animation keys are always atlas-prefixed (player-walk-s, player-idle-n, ...)
 */
export class Player extends Phaser.Physics.Arcade.Sprite {
  public tileX = 0
  public tileY = 0
  private lastFacing: Facing = 's'
  private readonly atlasKey = 'player'

  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y, 'player', 'idle-s')
    scene.add.existing(this)
    scene.physics.add.existing(this)
    this.setOrigin(0.5, 1.0) // foot anchor — RESEARCH.md Pitfall 4
    this.setSize(20, 10) // small AABB near feet for tile collision
    this.setOffset(6, 38) // offset body to feet of 32×48 sprite
    this.setCollideWorldBounds(false)
    const t = screenToWorld(x, y)
    this.tileX = t.tx
    this.tileY = t.ty
  }

  applyVector(v: { x: number; y: number }): void {
    this.setVelocity(v.x * SPEED, v.y * SPEED)
    const anim = animationKeyFromVector(v, this.lastFacing, this.atlasKey)
    if (anim.walking) this.lastFacing = anim.facing
    if (this.anims.currentAnim?.key !== anim.key) {
      this.anims.play(anim.key, true)
    }
    // ENGN-04: pixel snap on render position (physics body stays float internally).
    this.x = Math.round(this.x)
    this.y = Math.round(this.y)
    // ENGN-02 prerequisite: keep tile coords synced from the actual render position.
    const t = screenToWorld(this.x, this.y)
    this.tileX = t.tx
    this.tileY = t.ty
  }

  respawn(x: number, y: number): void {
    this.setVelocity(0, 0)
    this.setPosition(Math.round(x), Math.round(y))
    this.lastFacing = 's'
    this.anims.play(`${this.atlasKey}-idle-s`, true)
    const t = screenToWorld(this.x, this.y)
    this.tileX = t.tx
    this.tileY = t.ty
  }
}
